import { getAccessToken } from './supabase';
import type {
  ApiErrorBody,
  ApiErrorCode,
  PlanErrorFields,
  PlanFeature,
  PlanLimitKey,
  PlanName,
  ValidationErrorBody,
  ValidationErrorDetail,
} from '../types/api';

/**
 * Backend origin, trailing slashes trimmed.
 *
 * Exported because `EventSource` cannot go through {@link apiClient} — the SSE
 * stream builds its own URL. It still never carries the session token; SSE auth
 * is a short-lived per-request token minted through an authenticated call.
 */
export const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/+$/, '');

/**
 * Error thrown for any non-2xx backend response.
 *
 * `code` carries the domain error code from `{ detail, code }` bodies
 * (QUOTA_EXCEEDED, REJECTION_CAP_REACHED, ONBOARDING_INCOMPLETE, ...);
 * `validationErrors` carries FastAPI's 422 field details when present.
 * `plan` carries the extra fields of a 402 `PLAN_REQUIRED` / `PLAN_LIMIT_REACHED`
 * body (which feature, which cap, the plan that lifts it), null otherwise.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: ApiErrorCode | null;
  readonly validationErrors: ValidationErrorDetail[] | null;
  readonly plan: PlanErrorFields | null;

  constructor(
    message: string,
    status: number,
    code: ApiErrorCode | null,
    validationErrors: ValidationErrorDetail[] | null,
    plan: PlanErrorFields | null = null,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.validationErrors = validationErrors;
    this.plan = plan;
  }

  /** True when the backend rejected the call because onboarding is incomplete. */
  get isOnboardingIncomplete(): boolean {
    return this.code === 'ONBOARDING_INCOMPLETE';
  }

  /** 402: the feature is not in this account's plan (`plan.feature`, `plan.required_plan`). */
  get isPlanRequired(): boolean {
    return this.code === 'PLAN_REQUIRED';
  }

  /** 402: a plan cap is reached (`plan.limit_key`, `plan.limit`, `plan.next_*`). */
  get isPlanLimitReached(): boolean {
    return this.code === 'PLAN_LIMIT_REACHED';
  }
}

const PLAN_NAMES: readonly string[] = ['free', 'solo', 'studio', 'agency'];
const PLAN_FEATURES: readonly string[] = [
  'publish',
  'edit_posts',
  'assistant',
  'daily_news',
  'autopilot',
  'private_templates',
];
const PLAN_LIMIT_KEYS: readonly string[] = [
  'posts',
  'brands',
  'team_members',
  'example_posts',
  'products',
  'testimonials',
  'key_dates',
  'storage_bytes',
  'assistant_credits',
];

function isPlanName(value: unknown): value is PlanName {
  return typeof value === 'string' && PLAN_NAMES.includes(value);
}

function numberOrNull(value: unknown): number | null | undefined {
  if (value === null) return null;
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

/**
 * The 402 fields of a plan error body, each kept only when it has the wire
 * type. A field the backend did not send stays absent; an unknown feature or
 * limit key is dropped rather than trusted.
 */
function planErrorFields(body: ApiErrorBody): PlanErrorFields | null {
  if (body.code !== 'PLAN_REQUIRED' && body.code !== 'PLAN_LIMIT_REACHED') return null;
  const raw = body as unknown as Record<string, unknown>;
  const fields: PlanErrorFields = {};
  if (typeof raw.feature === 'string' && PLAN_FEATURES.includes(raw.feature)) {
    fields.feature = raw.feature as PlanFeature;
  }
  if (isPlanName(raw.required_plan)) fields.required_plan = raw.required_plan;
  if (typeof raw.limit_key === 'string' && PLAN_LIMIT_KEYS.includes(raw.limit_key)) {
    fields.limit_key = raw.limit_key as PlanLimitKey;
  }
  const limit = numberOrNull(raw.limit);
  if (limit !== undefined) fields.limit = limit;
  if (raw.next_plan === null || isPlanName(raw.next_plan)) fields.next_plan = raw.next_plan;
  const nextLimit = numberOrNull(raw.next_limit);
  if (nextLimit !== undefined) fields.next_limit = nextLimit;
  return fields;
}

export interface RequestOptions {
  /** Request body; serialised as JSON unless it is a FormData instance. */
  body?: unknown;
  /**
   * Query string parameters. Never pass auth tokens here. An array repeats the
   * key once per value (`?status=a&status=b`), which is how FastAPI reads a
   * list parameter; an empty array sends nothing.
   */
  query?: Record<string, string | number | boolean | readonly string[] | undefined | null>;
  /** Send without an Authorization header (health checks, etc.). */
  anonymous?: boolean;
  /**
   * Let the request outlive the page that started it. Used by deferred writes
   * flushed on `pagehide`, so a tab close or refresh still reaches the API.
   * Bodies are capped at 64 KB by the platform — keep it for small calls.
   */
  keepalive?: boolean;
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

function buildUrl(path: string, query: RequestOptions['query']): string {
  const url = new URL(`${API_BASE_URL}${path.startsWith('/') ? path : `/${path}`}`);
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined || value === null) continue;
      if (Array.isArray(value)) {
        for (const item of value as readonly string[]) url.searchParams.append(key, item);
        continue;
      }
      url.searchParams.set(key, String(value));
    }
  }
  return url.toString();
}

function isValidationErrorBody(body: unknown): body is ValidationErrorBody {
  return (
    typeof body === 'object' &&
    body !== null &&
    Array.isArray((body as { detail?: unknown }).detail)
  );
}

function isApiErrorBody(body: unknown): body is ApiErrorBody {
  return (
    typeof body === 'object' &&
    body !== null &&
    typeof (body as { detail?: unknown }).detail === 'string'
  );
}

/**
 * Turns a non-2xx response into an {@link ApiError}, preserving the domain
 * `code` and FastAPI's 422 field details.
 *
 * Exported because {@link downloadApiFile} cannot go through {@link apiClient} —
 * it reads a blob, not JSON — but its failures must surface the same codes
 * (`EXPORT_NOT_READY`, `REQUEST_NOT_COMPLETE`) as every other call.
 */
export async function toApiError(response: Response): Promise<ApiError> {
  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    // Non-JSON error body (proxy timeout, HTML error page). Fall through.
  }

  if (isValidationErrorBody(body)) {
    const first = body.detail[0];
    return new ApiError(
      first ? `${first.loc.join('.')}: ${first.msg}` : 'Validation failed.',
      response.status,
      null,
      body.detail,
    );
  }

  if (isApiErrorBody(body)) {
    return new ApiError(
      body.detail,
      response.status,
      body.code ?? null,
      null,
      planErrorFields(body),
    );
  }

  return new ApiError(
    `Request failed with status ${response.status}.`,
    response.status,
    null,
    null,
  );
}

/**
 * The error for a request that never got an answer: `fetch` rejects with a
 * `TypeError` when there is no network, the host is unreachable or a CORS
 * preflight fails. Said in the product's voice rather than as the browser's
 * "Failed to fetch", and told apart from a server error by `status` 0 and the
 * client-only `NETWORK_UNAVAILABLE` code. An abort is not one of these: it is
 * a `DOMException` and passes through untouched.
 */
export function networkError(): ApiError {
  const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
  return new ApiError(
    offline
      ? 'No connection. Check your network and try again.'
      : 'Could not reach Autopostmate. Check your connection and try again.',
    0,
    'NETWORK_UNAVAILABLE',
    null,
  );
}

/**
 * Typed fetch wrapper for the FastAPI backend.
 *
 * Attaches the Supabase access token as an `Authorization: Bearer` header.
 * The session token must never appear in a URL — SSE streams authenticate with
 * a short-lived, single-request token minted through an authenticated call.
 */
async function request<TResponse>(
  method: string,
  path: string,
  options: RequestOptions = {},
): Promise<TResponse> {
  const headers: Record<string, string> = { Accept: 'application/json', ...options.headers };

  if (!options.anonymous) {
    const token = await getAccessToken();
    if (!token) {
      throw new ApiError('Not signed in.', 401, null, null);
    }
    headers.Authorization = `Bearer ${token}`;
  }

  let payload: BodyInit | undefined;
  if (options.body instanceof FormData) {
    // Let the browser set the multipart boundary.
    payload = options.body;
  } else if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    payload = JSON.stringify(options.body);
  }

  const init: RequestInit = { method, headers };
  if (payload !== undefined) init.body = payload;
  if (options.signal) init.signal = options.signal;
  if (options.keepalive) init.keepalive = true;

  let response: Response;
  try {
    response = await fetch(buildUrl(path, options.query), init);
  } catch (caught) {
    if (caught instanceof TypeError) throw networkError();
    throw caught;
  }

  if (!response.ok) {
    throw await toApiError(response);
  }

  if (response.status === 204) {
    return undefined as TResponse;
  }

  return (await response.json()) as TResponse;
}

export const apiClient = {
  get: <TResponse>(path: string, options?: RequestOptions) =>
    request<TResponse>('GET', path, options),
  post: <TResponse>(path: string, options?: RequestOptions) =>
    request<TResponse>('POST', path, options),
  patch: <TResponse>(path: string, options?: RequestOptions) =>
    request<TResponse>('PATCH', path, options),
  put: <TResponse>(path: string, options?: RequestOptions) =>
    request<TResponse>('PUT', path, options),
  delete: <TResponse>(path: string, options?: RequestOptions) =>
    request<TResponse>('DELETE', path, options),
};
